import type { SearchProperty } from '@/api/types/hor'

import { TONNES_TO_KG } from './constants'

import type {
  EmissionHotelBatchResponse,
  EmissionPropertyBatchResponse,
  EmissionResponse,
  PostHotelBatchCarbonEmissionParams,
  PostPropertyBatchCarbonEmissionParams,
  TransformedHotelBatchCarbonEmissionResponse,
  TransformedPropertyBatchCarbonEmissionResponse,
} from './types'

const transformSegment = (segment: EmissionResponse) => ({
  ...segment,
  emissionsInKg: segment.tonnes_of_emissions * TONNES_TO_KG,
})

const transformEmissionBatchResponse = <
  U extends { segments: EmissionResponse[] },
  T extends { segments: EmissionResponse[] },
>(
  response: U
): T =>
  ({
    segments: response.segments.map(transformSegment),
  }) as unknown as T

export const transformEmissionHotelBatchResponse = (
  response: EmissionHotelBatchResponse
): TransformedHotelBatchCarbonEmissionResponse =>
  transformEmissionBatchResponse<
    EmissionHotelBatchResponse,
    TransformedHotelBatchCarbonEmissionResponse
  >(response)

export const transformEmissionPropertyBatchResponse = (
  response: EmissionPropertyBatchResponse
): TransformedPropertyBatchCarbonEmissionResponse =>
  transformEmissionBatchResponse<
    EmissionPropertyBatchResponse,
    TransformedPropertyBatchCarbonEmissionResponse
  >(response)

export const getPropertyEmissionsRequestBody = ({
  properties,
  nights = 1,
  travellers = 1,
}: {
  properties: SearchProperty[]
  nights?: number
  travellers?: number
}): PostPropertyBatchCarbonEmissionParams => ({
  segments: [
    (properties || []).map((property) => ({
      id: property.id,
      type: 'vacation-rental' as const,
      nights,
      travellers,
    })),
  ],
})

export const getHotelEmissionsRequestBody = ({
  properties,
  nights = 1,
  rooms = 1,
}: {
  properties: SearchProperty[]
  nights?: number
  rooms?: number
}): PostHotelBatchCarbonEmissionParams => ({
  segments: [
    (properties || []).map((property) => ({
      id: property.id,
      type: 'hotel' as const,
      nights,
      rooms,
      location: {
        name: property?.title,
        address_line_1: property?.address,
      },
    })),
  ],
})
